import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, XCircle, Mail, User, Building2, Eye, EyeOff } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export default function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [token] = useState(() => searchParams.get('token') || window.history.state?.evaToken || '');

  // EIP-433: the token is a single-use secret that arrived in the URL, so it
  // must not stay in the address bar, the history entry, or any Referer this
  // page sends. Captured into state first so everything below keeps working,
  // then also parked in history.state so a refresh mid-form still has it
  // instead of landing on the invalid-link card. Raw replaceState rather than
  // the router's navigate: this rewrites the current entry only and leaves
  // react-router's own state (idx, key) intact by spreading it.
  useEffect(() => {
    if (!searchParams.get('token') || !token) return;
    window.history.replaceState(
      { ...window.history.state, evaToken: token },
      document.title,
      window.location.pathname
    );
  }, [searchParams, token]);

  const peopleLoginUrl = process.env.REACT_APP_EVA_PEOPLE_URL
    ? `${process.env.REACT_APP_EVA_PEOPLE_URL.replace(/\/$/, '')}/login` : null;
  const recovery = <p className="mt-4 text-sm text-muted-foreground">Already completed setup? Sign in with the email and password you created.{peopleLoginUrl && <> <a className="text-primary underline" href={peopleLoginUrl}>Sign in to Eva People</a></>}</p>;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [peopleSession, setPeopleSession] = useState(null);
  const [invitationData, setInvitationData] = useState(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    password: '',
    confirm_password: '',
  });
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(window.history.state?.evaInvitationAccepted ? 'Your account was created successfully.' : 'No invitation token provided');
      setLoading(false);
      return;
    }

    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await api.post('/auth/invitation/validate', { token });
      setInvitationData(response.data);
      // Pre-fill the name fields with what the admin entered at creation time so the
      // invitee doesn't have to retype it (EIP-209). They remain editable.
      setFormData((prev) => ({
        ...prev,
        first_name: response.data.first_name || prev.first_name,
        last_name: response.data.last_name || prev.last_name,
      }));
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invitation link');
      setLoading(false);
    }
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push('At least 8 characters');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('One lowercase letter');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('One uppercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('One number');
    }
    if (!/[@$!%*?&#^()_+\-=[\]{}|;:,.<>]/.test(password)) {
      errors.push('One special character');
    }
    return errors;
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setFormData({ ...formData, password: newPassword });
    setPasswordErrors(validatePassword(newPassword));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setError('Please enter your first and last name');
      return;
    }

    if (passwordErrors.length > 0) {
      setError('Please fix password requirements');
      return;
    }

    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      // Get the product from invitation data (defaults to eva_insights)
      const product = invitationData?.product || 'eva_insights';
      
      const response = peopleSession ? { data: peopleSession } : await api.post('/auth/invitation/accept', {
        token,
        first_name: formData.first_name,
        last_name: formData.last_name,
        password: formData.password,
        product: product,
      });

      if (product === 'eva_people') {
        // Keep this session in memory until the existing one-use SSO handoff succeeds.
        // Cross-origin localStorage cannot sign the employee into People.
        setPeopleSession(response.data);
        // The consumed invitation is no longer needed in this history entry.
        const { evaToken, ...historyState } = window.history.state || {};
        window.history.replaceState({ ...historyState, evaInvitationAccepted: true }, document.title, window.location.pathname);
        const handoff = await api.post('/auth/eva-people/sso-link', {}, {
          headers: { Authorization: `Bearer ${response.data.access_token}` },
        });
        window.location.href = handoff.data.url;
      } else {
        setSession(response.data);
        window.location.href = '/overview';
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not finish setup. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !invitationData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2">
              {window.history.state?.evaInvitationAccepted ? <CheckCircle className="h-6 w-6 text-primary" /> : <XCircle className="h-6 w-6 text-destructive" />}
              <CardTitle>{window.history.state?.evaInvitationAccepted ? 'Account created' : 'Invalid Invitation'}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{error}</p>
            {recovery}
            {!window.history.state?.evaInvitationAccepted && <Button onClick={() => navigate('/login')} className="w-full mt-4">
              Go to Insights Login
            </Button>}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl text-center">You&apos;re Invited!</CardTitle>
          <CardDescription className="text-center">
            Complete your account setup to join <strong>{invitationData?.tenant_name}</strong>
          </CardDescription>
        </CardHeader>

        <CardContent>
          {peopleSession && recovery}
          <div className="mb-6 p-4 bg-secondary/50 rounded-lg space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Email:</span>
              <span className="font-medium min-w-0 break-all">{invitationData?.email}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Role:</span>
              <span className="font-medium capitalize">{invitationData?.role}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Organization:</span>
              <span className="font-medium min-w-0 break-words">{invitationData?.tenant_name}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Product:</span>
              <span className="font-medium">{invitationData?.product === 'eva_people' ? 'Eva People' : 'Eva Insights'}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handlePasswordChange}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {formData.password && passwordErrors.length > 0 && (
                <div className="text-xs space-y-1 mt-2">
                  <p className="text-muted-foreground font-medium">Password must contain:</p>
                  <ul className="space-y-1">
                    {passwordErrors.map((err, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-destructive">
                        <XCircle className="h-3 w-3" />
                        {err}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {formData.password && passwordErrors.length === 0 && (
                <div className="text-xs flex items-center gap-2 text-success mt-2">
                  <CheckCircle className="h-3 w-3" />
                  Password meets all requirements
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm Password *</Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {formData.confirm_password && formData.password !== formData.confirm_password && (
                <p className="text-xs text-destructive flex items-center gap-2">
                  <XCircle className="h-3 w-3" />
                  Passwords do not match
                </p>
              )}
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" className="w-full" disabled={submitting || passwordErrors.length > 0}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting up account...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  {peopleSession ? 'Continue to Eva People' : 'Complete Setup'}
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/login')}>
              Sign in
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
